import React, { useEffect, useState, useRef } from 'react';
import { Box, Container, Typography, Grid, Button, Card, CardMedia, CardContent, Chip, IconButton, Tooltip, Fade, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme, useMediaQuery, alpha } from '@mui/material/styles';
import { Schedule, ArrowForward, WhatsApp, Phone, LocalHospital, ElderlyWoman, MedicalServices, SportsHandball, PregnantWoman, Healing, ChildCare, WorkOutline, Devices, SportsSoccer, Coronavirus, Psychology, Air, Balance, Favorite, Add } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { blogPosts as defaultBlogPosts } from './pages/Blogs';

// Import sections
import HeroSection from './sections/HeroSection';
import AboutSection from './sections/AboutSection';
import TestimonialsSection from './sections/TestimonialsSection';

// Import components and data
import ServiceCard from './cards/ServiceCard';
import { services } from './data/services';

const Home = () => {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.target?.dataset?.section) {
          const sectionId = entry.target.dataset.section;
          setVisibleSections(prev => {
            const newSet = new Set(prev);
            if (entry.isIntersecting) {
              newSet.add(sectionId);
            } else {
              newSet.delete(sectionId);
            }
            return newSet;
          });
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '50px'
    });

    sectionRefs.current.forEach((ref, sectionId) => {
      if (ref && typeof sectionId === 'string') {
        ref.dataset.section = sectionId;
        observer.observe(ref);
      }
    });

    return () => observer?.disconnect();
  }, []);

  // Helper function to check if a section is visible
  const checkSectionVisibility = (sectionId) => visibleSections.has(sectionId);

  // Add refs to the Map
  const addSectionRef = (sectionId, ref) => {
    if (ref) {
      sectionRefs.current.set(sectionId, ref);
    }
  };

  // Map of service titles to their component paths - Updated with correct paths
  const serviceRoutes = {
    'Sports Physiotherapy': '/services/SportsPhysiotherapy',
    'Orthopedic Physiotherapy': '/services/OrthopedicPhysiotherapy',
    'Manual Therapy': '/services/ManualTherapy',
    'Cardiac Rehabilitation': '/services/CardiacRehabilitation',
    'Women\'s Health': '/services/WomensHealth',
    'Vestibular Rehabilitation': '/services/VestibularRehabilitation',
    'Home Care Services': '/services/HomeCareServices',
    'Geriatric Physiotherapy': '/services/GeriatricPhysiotherapy',
    'Neurological Rehabilitation': '/services/NeurologicalRehabilitation'
  };

  return (
    <Box sx={{ 
      overflow: 'hidden',
      contain: 'content',
    }}>
      <HeroSection />
      
      {/* Featured Services Section */}
      <Box
        ref={(ref) => addSectionRef('services', ref)}
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, rgba(0,150,136,0.05) 0%, rgba(255,255,255,1) 100%)',
          position: 'relative',
          overflow: 'hidden',
          contain: 'paint layout',
          transform: 'translateZ(0)'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.4,
            background: 'radial-gradient(circle at 25% 25%, rgba(0, 150, 136, 0.1) 0%, transparent 40%), radial-gradient(circle at 75% 75%, rgba(0, 150, 136, 0.1) 0%, transparent 40%)',
            zIndex: 0
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box 
            ref={(ref) => addSectionRef('services-title', ref)}
            sx={{ 
              textAlign: 'center', 
              mb: { xs: 6, md: 8 },
              opacity: checkSectionVisibility('services-title') ? 1 : 0,
              transform: checkSectionVisibility('services-title') ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ 
                color: 'primary.main', 
                mb: 2,
                fontWeight: 600,
                letterSpacing: 1.2,
                textTransform: 'uppercase'
              }}
            >
              Our Services
            </Typography>
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Comprehensive Physiotherapy Solutions
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8
              }}
            >
              Discover our range of specialized physiotherapy services designed to help you recover, improve, and maintain optimal physical health.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.slice(0, 6).map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={service.title}>
                <Box
                  sx={{
                    opacity: checkSectionVisibility('services') ? 1 : 0,
                    transform: checkSectionVisibility('services') ? 'translateY(0)' : 'translateY(50px)',
                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    transitionDelay: `${index * 100}ms`
                  }}
                >
                  <ServiceCard 
                    service={service}
                    delay={index * 100}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 8 } }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/services')}
              sx={{
                px: 6,
                py: 2,
                background: 'linear-gradient(45deg, #00796b 30%, #009688 90%)',
                boxShadow: '0 4px 20px rgba(0,150,136,0.4)',
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '1rem', md: '1.1rem' },
                '&:hover': {
                  background: 'linear-gradient(45deg, #00695c 30%, #00897b 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0,150,136,0.5)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              View All Services
            </Button>
          </Box>
        </Container>
      </Box>

      <AboutSection />
      
      <TestimonialsSection />

      {/* Blog Titles Section */}
      <Box
        ref={(ref) => addSectionRef('blog-titles', ref)}
        sx={{
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(0,150,136,0.05) 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              textAlign: 'center', 
              mb: { xs: 4, md: 6 }
            }}
          >
            <Typography
              variant="overline"
              sx={{ 
                color: 'primary.main', 
                mb: 1,
                fontWeight: 600,
                letterSpacing: 1,
                fontSize: '0.75rem'
              }}
            >
              Latest Articles
            </Typography>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Featured Blog Posts
            </Typography>
          </Box>

          <List sx={{ maxWidth: 800, mx: 'auto' }}>
            {defaultBlogPosts.slice(0, 5).map((post, index) => (
              <ListItem
                key={post.id}
                sx={{
                  opacity: checkSectionVisibility('blog-titles') ? 1 : 0,
                  transform: checkSectionVisibility('blog-titles') ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s`,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
              >
                <ListItemButton
                  onClick={() => navigate('/blogs')}
                  sx={{
                    py: 2,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: 'rgba(0, 150, 136, 0.08)',
                      transform: 'translateX(8px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ListItemIcon>
                    <post.icon sx={{ color: post.iconColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={post.title}
                    secondary={post.category}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: 'text.primary'
                    }}
                    secondaryTypographyProps={{
                      color: 'primary.main',
                      fontWeight: 500
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              color="primary"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/blogs')}
              sx={{
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                borderWidth: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,150,136,0.2)'
                }
              }}
            >
              View All Articles
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;